import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import axios from 'axios';
import { Instagram, Facebook, MapPin, Phone, Clock, Mail, ArrowRight, ArrowLeft, Menu as MenuIcon, X, Check, Quote, Sparkles } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Toaster, toast } from 'sonner';

import { SHOP } from '@/constants/testIds';
import { MENU_CATEGORIES, CAKES_CATEGORIES, GALLERY, HERO_IMAGE, HERO_ACCENT_IMAGE, ORDER_OCCASIONS, SHOP_INFO, TESTIMONIALS, PRALINY_IMAGES } from '@/data/menu';
import { LanguageProvider, useLang, LanguageToggle } from '@/i18n';

import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FADE_UP = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
};

const NAV = [
  { id: 'menu', label: 'Menu', testId: SHOP.navMenu },
  { id: 'praliny', label: 'Praliny', testId: SHOP.navPraliny },
  { id: 'zamowienia', label: 'Ciasta i torty na zamówienie', testId: SHOP.navOrders },
  { id: 'kontakt', label: 'Kontakt', testId: SHOP.navContact },
];

function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid={SHOP.navBrand}
          onClick={() => go('home')}
          className="font-display text-xl md:text-2xl tracking-tight text-white hover:text-[#c07e5b] transition-colors duration-500"
        >
          {t('Strona główna.').replace('.', '')}<span className="text-[#c07e5b]">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testId}
              onClick={() => go(n.id)}
              className="text-sm tracking-wide text-zinc-300 hover:text-[#c07e5b] transition-colors duration-300"
            >
              {t(n.label)}
            </button>
          ))}
          <a
            href={`tel:${SHOP_INFO.phoneRaw}`}
            className="text-sm tracking-wide text-[#c07e5b] hover:text-[#d9a07d] transition-colors duration-300"
          >
            {SHOP_INFO.phone}
          </a>
          <LanguageToggle />
        </nav>

        <button
          data-testid={SHOP.navMobileToggle}
          onClick={() => setOpen(!open)}
          className="md:hidden text-white"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <MenuIcon size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/5 overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  className="text-left font-display text-3xl text-white hover:text-[#c07e5b] transition-colors"
                >
                  {t(n.label)}
                </button>
              ))}
              <a href={`tel:${SHOP_INFO.phoneRaw}`} className="text-[#c07e5b] tracking-wide text-sm">
                {SHOP_INFO.phone}
              </a>
              <div className="pt-4 border-t border-white/5">
                <LanguageToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative h-[100svh] w-full overflow-hidden flex items-end"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMAGE}
          alt="Czekoladziarnia Wrocław — degustacja gorących czekolad przed lokalem"
          className="w-full h-full object-cover object-[center_25%]"
        />
        {/* Layered darkening for text legibility — lighter on the right to reveal the storefront */}
        <div className="absolute inset-0 bg-[#0a0a0a]/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/10 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0a0a0a]/60 to-[#0a0a0a]/10 md:from-[#0a0a0a]/90 md:via-[#0a0a0a]/35 md:to-transparent" />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      {/* Floating accent card — the branded praline box */}
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: -3 }}
        animate={{ opacity: 1, y: 0, rotate: 3 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
        style={{ opacity }}
        className="hidden lg:block absolute right-10 xl:right-20 bottom-32 xl:bottom-36 w-56 xl:w-64 aspect-[3/4] overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] ring-1 ring-white/10 z-[5]"
      >
        <img
          src={HERO_ACCENT_IMAGE}
          alt="Bombonierka Czekoladziarnia Wrocław"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <p className="label-eyebrow text-white/80 text-[9px]">Bombonierka autorska</p>
          <p className="font-display text-white text-lg mt-1 leading-tight">Ręcznie pakowana</p>
        </div>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="label-eyebrow"
        >
          {t('Wrocław · ul. Więzienna 31 · od 2004')}
        </motion.p>
        <motion.h1
          data-testid={SHOP.heroTitle}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="font-display text-white mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1] tracking-tight text-balance max-w-4xl"
        >
          {t('Najstarsza Czekoladziarnia')} <em className="text-[#c07e5b] not-italic font-light italic">{t('we Wrocławiu.')}</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 font-light leading-relaxed"
        >
          {t('Od ponad 20 lat w sercu Starego Miasta łączymy miłość do czekolady z rzemieślniczą pasją. Słyniemy z gęstej, prawdziwej gorącej czekolady – serwowanej klasycznie lub z autorskimi i sezonowymi dodatkami.')}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
          className="mt-5 max-w-2xl text-base md:text-lg text-zinc-300 font-light leading-relaxed"
        >
          {t('Naszą największą dumą są ręcznie robione praliny, z których każda to małe dzieło sztuki. ')}<strong className="text-white font-semibold">{t('W menu znajdziecie również pozycje bezglutenowe.')}</strong>{t(' Odwiedź nas, by skosztować wyśmienitych deserów, znaleźć idealny prezent i stworzyć niezapomniane, czekoladowe wspomnienia.')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <button
            data-testid={SHOP.heroCta}
            onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-copper px-8 py-4 text-sm tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-3"
          >
            {t('Zobacz menu')} <ArrowRight size={16} />
          </button>
          <button
            data-testid={SHOP.heroSecondaryCta}
            onClick={() => document.getElementById('zamowienia')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-ghost-copper px-8 py-4 text-sm tracking-[0.18em] uppercase font-semibold"
          >
            {t('Zamówienia indywidualne')}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-3 text-zinc-500"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center mt-12">{t('SCROLL')}</span>
      </motion.div>
    </section>
  );
}

function MenuSection() {
  const { t } = useLang();
  const [active, setActive] = useState(MENU_CATEGORIES[0].id);
  const category = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <section id="menu" data-testid={SHOP.menuSection} className="relative bg-[#050505] py-32 md:py-48">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...FADE_UP} className="max-w-3xl">
          <p className="label-eyebrow">{t('Karta · 2026')}</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-6 leading-[0.95] tracking-tight">
            {t('Menu, które zmienia się ')}<em className="text-[#c07e5b] italic font-light">{t('jak sezony.')}</em>
          </h2>
          <p className="mt-8 text-zinc-400 text-lg leading-relaxed font-light">
            {t('Część pozycji jest stała — klasyczna gorąca czekolada, brownie z wiśniami, sygnaturowa pralina „Esencja Czekolady". Reszta podąża za sezonem.')}
          </p>
        </motion.div>

        <div className="mt-16 flex flex-nowrap md:flex-wrap gap-2 border-b border-white/5 pb-6 overflow-x-auto scrollbar-hide -mx-6 md:mx-0 px-6 md:px-0">
          {MENU_CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`${SHOP.menuCategoryButton}-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`flex-shrink-0 px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-semibold transition-all duration-500 whitespace-nowrap ${
                active === c.id
                  ? 'bg-[#c07e5b] text-[#0a0a0a]'
                  : 'text-zinc-400 hover:text-[#c07e5b]'
              }`}
            >
              {t(c.label)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16"
          >
            <div className="lg:col-span-5 relative aspect-[4/5] overflow-hidden lg:sticky lg:top-32">
              <img
                src={category.image}
                alt={t(category.label)}
                className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1.6s] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="label-eyebrow text-white/70">{t(category.tagline)}</p>
                <p className="font-display text-2xl text-white mt-1">{t(category.label)}</p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="text-zinc-400 text-lg leading-relaxed font-light mb-6">
                {t(category.description)}
              </p>
              {category.priceHint && (
                <p className="label-eyebrow text-[#c07e5b]/80 mb-8">{t(category.priceHint)}</p>
              )}
              <ul className="divide-y divide-white/5">
                {category.items.map((it, i) => (
                  <motion.li
                    key={it.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-start justify-between gap-4 md:gap-6 py-5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-xl md:text-2xl text-white tracking-tight leading-tight">
                        {t(it.name)}
                      </p>
                      {it.description && (
                        <p className="text-zinc-500 text-sm mt-1.5 leading-relaxed font-light">
                          {t(it.description)}
                        </p>
                      )}
                    </div>
                    {it.price && (
                      <span className="text-[#c07e5b] tracking-wide text-sm md:text-base whitespace-nowrap font-medium mt-1">
                        {it.price}
                      </span>
                    )}
                  </motion.li>
                ))}
              </ul>
              {category.note && (
                <p className="mt-10 text-zinc-500 text-sm italic leading-relaxed border-l-2 border-[#c07e5b]/40 pl-4">
                  {t(category.note)}
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function Gallery() {
  const { t } = useLang();
  return (
    <section className="bg-[#0a0a0a] py-24 md:py-32 overflow-hidden">
      <motion.div {...FADE_UP} className="max-w-7xl mx-auto px-6 md:px-12 mb-12 flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="label-eyebrow">@czekoladziarniawroclaw</p>
          <h3 className="font-display text-3xl md:text-5xl text-white mt-4 leading-tight">
            {t('Świeżo z naszej pracowni.')}
          </h3>
        </div>
        <a
          href={SHOP_INFO.instagram}
          target="_blank"
          rel="noreferrer"
          className="text-[#c07e5b] hover:text-[#d9a07d] transition-colors text-sm tracking-[0.18em] uppercase inline-flex items-center gap-2"
        >
          {t('Instagram')} <ArrowRight size={14} />
        </a>
      </motion.div>
      <div className="flex gap-4 md:gap-6 overflow-x-auto pb-6 px-6 md:px-12 snap-x snap-mandatory scrollbar-hide">
        {GALLERY.map((src, i) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="flex-shrink-0 w-72 md:w-96 aspect-square snap-center overflow-hidden"
          >
            <img
              src={src}
              alt="Czekoladziarnia Wrocław"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1.6s] ease-out"
            />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CakesMenu() {
  const { t } = useLang();
  const [active, setActive] = useState(CAKES_CATEGORIES[0].id);
  const category = CAKES_CATEGORIES.find((c) => c.id === active);

  return (
    <div data-testid="cakes-menu" className="mt-16 md:mt-20">
      <div className="flex flex-nowrap md:flex-wrap gap-2 border-b border-white/5 pb-6 overflow-x-auto scrollbar-hide -mx-6 md:mx-0 px-6 md:px-0">
        {CAKES_CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`${SHOP.cakesCategoryButton}-${c.id}`}
            onClick={() => setActive(c.id)}
            className={`flex-shrink-0 px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-semibold transition-all duration-500 whitespace-nowrap ${
              active === c.id
                ? 'bg-[#c07e5b] text-[#0a0a0a]'
                : 'text-zinc-400 hover:text-[#c07e5b]'
            }`}
          >
            {t(c.label)}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={category.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16"
        >
          <div className="lg:col-span-5 relative aspect-[4/5] overflow-hidden lg:sticky lg:top-32">
            <img
              src={category.image}
              alt={t(category.label)}
              className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1.6s] ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="label-eyebrow text-white/70">{t(category.tagline)}</p>
              <p className="font-display text-2xl text-white mt-1">{t(category.label)}</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed font-light mb-6">
              {t(category.description)}
            </p>
            {category.priceHint && (
              <p className="label-eyebrow text-[#c07e5b]/80 mb-8">{t(category.priceHint)}</p>
            )}
            <ul className="divide-y divide-white/5">
              {category.items.map((it, i) => (
                <motion.li
                  key={it.name}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-start justify-between gap-4 md:gap-6 py-5"
                >
                  <p className="flex-1 min-w-0 font-display text-xl md:text-2xl text-white tracking-tight leading-tight">
                    {t(it.name)}
                  </p>
                  <span className="text-[#c07e5b] tracking-wide text-sm md:text-base whitespace-nowrap font-medium mt-1">
                    {t(it.price)}
                  </span>
                </motion.li>
              ))}
            </ul>
            {category.note && (
              <p className="mt-10 text-zinc-500 text-sm italic leading-relaxed border-l-2 border-[#c07e5b]/40 pl-4">
                {t(category.note)}
              </p>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}


function OrdersForm() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    occasion: '',
    delivery_date: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.occasion || !form.description) {
      toast.error(t('Uzupełnij wszystkie wymagane pola.'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, form);
      setDone(true);
      toast.success(t('Dziękujemy! Odpowiemy w ciągu 24 godzin.'));
      setForm({ name: '', email: '', phone: '', occasion: '', delivery_date: '', description: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || t('Nie udało się wysłać zapytania. Spróbuj ponownie lub zadzwoń.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="zamowienia" data-testid={SHOP.ordersSection} className="relative bg-[#050505] py-32 md:py-48">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...FADE_UP} className="max-w-3xl">
          <p className="label-eyebrow">{t('Ciasta i torty na zamówienie')}</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-6 leading-[0.95] tracking-tight">
            {t('Twoja okazja, ')}<em className="text-[#c07e5b] italic font-light">{t('nasza receptura.')}</em>
          </h2>
          <p className="mt-8 text-zinc-400 text-base md:text-lg leading-relaxed font-light">
            {t('Każdego dnia w naszej witrynie czekają na Ciebie świeże ciasta – od klasycznych wypieków po sezonowe nowości, które idealnie komponują się z kawą czy gorącą czekoladą. Z myślą o osobach z celiakią zawsze oferujemy w 100% bezpieczne ciasta bezglutenowe, przygotowywane w osobnej pracowni. Wszystkie nasze wypieki możesz zamówić w całości (oferta poniżej). Tworzymy również personalizowane torty na wesela, chrzciny, komunie i urodziny – smak, dekorację oraz wycenę ustalamy indywidualnie.')}
          </p>
          <p className="mt-6 text-white text-base md:text-lg leading-relaxed font-medium">
            {t('Zamówienia przyjmujemy telefonicznie lub przez Messenger!')}
          </p>

          <div className="mt-10 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-6">
            <a
              href={`tel:${SHOP_INFO.phoneRaw}`}
              className="font-display text-3xl md:text-4xl text-[#c07e5b] hover:text-[#d9a07d] transition-colors"
            >
              {SHOP_INFO.phone}
            </a>
            <a
              href="https://m.me/CzekoladziarniaWroclaw"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost-copper px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-2 whitespace-nowrap self-start"
            >
              {t('Napisz na Messengerze')} <ArrowRight size={14} />
            </a>
          </div>
        </motion.div>

        {/* Bakery menu — right below the description */}
        <CakesMenu />

        {/* Order form — after the bakery menu */}
        <motion.div {...FADE_UP} transition={{ ...FADE_UP.transition, delay: 0.1 }} className="mt-20 md:mt-28 max-w-3xl mx-auto">
          <div className="bg-[#120f0e]/60 backdrop-blur-md border border-white/5 p-8 md:p-12">
            {done ? (
              <div data-testid={SHOP.orderFormSuccess} className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#c07e5b]/10 border border-[#c07e5b]/30 flex items-center justify-center">
                  <Check className="text-[#c07e5b]" size={28} />
                </div>
                <h3 className="font-display text-4xl text-white mt-8">{t('Dziękujemy.')}</h3>
                <p className="text-zinc-400 mt-4 max-w-md mx-auto leading-relaxed">
                  {t('Otrzymaliśmy Twoje zapytanie i wysłaliśmy potwierdzenie na Twój e-mail. Skontaktujemy się z Tobą w ciągu 24 godzin.')}
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="btn-ghost-copper mt-10 px-6 py-3 text-xs tracking-[0.18em] uppercase"
                >
                  {t('Wyślij kolejne zapytanie')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="label-eyebrow">{t('Imię i nazwisko')}</Label>
                    <Input
                      id="name"
                      data-testid={SHOP.orderFormName}
                      value={form.name}
                      onChange={update('name')}
                      className="mt-3 bg-transparent border-0 border-b border-white/10 rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:border-[#c07e5b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="label-eyebrow">{t('E-mail')}</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid={SHOP.orderFormEmail}
                      value={form.email}
                      onChange={update('email')}
                      className="mt-3 bg-transparent border-0 border-b border-white/10 rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:border-[#c07e5b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="label-eyebrow">{t('Telefon')}</Label>
                    <Input
                      id="phone"
                      data-testid={SHOP.orderFormPhone}
                      value={form.phone}
                      onChange={update('phone')}
                      className="mt-3 bg-transparent border-0 border-b border-white/10 rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:border-[#c07e5b]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="delivery_date" className="label-eyebrow">{t('Data odbioru')}</Label>
                    <Input
                      id="delivery_date"
                      type="date"
                      data-testid={SHOP.orderFormDate}
                      value={form.delivery_date}
                      onChange={update('delivery_date')}
                      className="mt-3 bg-transparent border-0 border-b border-white/10 rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:border-[#c07e5b] [color-scheme:dark]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="label-eyebrow">{t('Okazja')}</Label>
                  <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v })}>
                    <SelectTrigger
                      data-testid={SHOP.orderFormOccasion}
                      className="mt-3 bg-transparent border-0 border-b border-white/10 rounded-none px-0 h-12 text-white text-base focus:ring-0 focus:border-[#c07e5b] data-[placeholder]:text-zinc-500"
                    >
                      <SelectValue placeholder={t('Wybierz okazję')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#120f0e] border-white/10 text-white">
                      {ORDER_OCCASIONS.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-[#c07e5b]/20 focus:text-white">
                          {t(o)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description" className="label-eyebrow">{t('Opisz, co masz w głowie')}</Label>
                  <Textarea
                    id="description"
                    data-testid={SHOP.orderFormDescription}
                    value={form.description}
                    onChange={update('description')}
                    rows={5}
                    placeholder={t('Liczba osób, smaki, alergeny, inspiracje, sposób pakowania…')}
                    className="mt-3 bg-transparent border border-white/10 rounded-none text-white text-base placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[#c07e5b] resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid={SHOP.orderFormSubmit}
                  disabled={submitting}
                  className="btn-copper w-full md:w-auto px-10 py-4 text-sm tracking-[0.18em] uppercase font-semibold inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t('Wysyłam…') : t('Wyślij zapytanie')}
                  {!submitting && <ArrowRight size={16} />}
                </button>

                <p className="text-zinc-600 text-xs leading-relaxed pt-2">
                  {t('Wysyłając formularz, wyrażasz zgodę na kontakt w sprawie realizacji zamówienia.')}
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    'Belgijska czekolada',
    '·',
    'Ręcznie robione',
    '·',
    'Od 2004 roku',
    '·',
    'ul. Więzienna 31',
    '·',
    'Limitowane serie sezonowe',
    '·',
  ];
  const line = [...items, ...items, ...items];
  return (
    <section aria-hidden className="bg-[#050505] border-y border-white/5 py-6 md:py-10 overflow-hidden">
      <div className="flex gap-10 whitespace-nowrap animate-[marquee_40s_linear_infinite]">
        {line.map((t, i) => (
          <span
            key={i}
            className={`font-display text-3xl md:text-5xl lg:text-6xl ${
              t === '·' ? 'text-[#c07e5b]' : 'text-zinc-200'
            } italic font-light`}
          >
            {t}
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }`}</style>
    </section>
  );
}

function Testimonials() {
  const { t } = useLang();
  return (
    <section data-testid={SHOP.testimonialsSection} className="bg-[#0a0a0a] py-32 md:py-44 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...FADE_UP} className="max-w-3xl">
          <p className="label-eyebrow">{t('Słowo o nas')}</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-6 leading-[0.95] tracking-tight">
            {t('Goście, których ')}<em className="text-[#c07e5b] italic font-light">{t('pamiętamy.')}</em>
          </h2>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {TESTIMONIALS.map((tm, i) => (
            <motion.figure
              key={tm.author}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#120f0e]/60 backdrop-blur-md border border-white/5 p-8 md:p-12 hover:border-[#c07e5b]/30 transition-colors duration-700"
            >
              <Quote className="text-[#c07e5b]" size={28} strokeWidth={1.2} />
              <blockquote className="font-display text-2xl md:text-3xl text-white mt-6 leading-[1.3] tracking-tight">
                &bdquo;{t(tm.quote)}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/5">
                <p className="text-white text-sm tracking-wide">{t(tm.author)}</p>
                <p className="text-zinc-500 text-xs mt-1 tracking-[0.18em] uppercase">{t(tm.role)}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}


function Newsletter() {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('Podaj poprawny adres e-mail.'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      if (res.data.status === 'already_subscribed') {
        toast.info(t('Jesteś już zapisany. Do zobaczenia w skrzynce.'));
      } else {
        toast.success(t('Witamy! Sprawdź skrzynkę.'));
      }
      setDone(true);
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Nie udało się zapisać. Spróbuj ponownie.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-white/5 overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.p {...FADE_UP} className="label-eyebrow inline-flex items-center gap-2 justify-center">
          <Sparkles size={12} /> {t('Limitowane serie')}
        </motion.p>
        <motion.h3
          {...FADE_UP}
          transition={{ ...FADE_UP.transition, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-tight tracking-tight"
        >
          {t('Wiedz pierwszy, ')}<em className="text-[#c07e5b] italic font-light">{t('kiedy znikają z gablot.')}</em>
        </motion.h3>
        <motion.p
          {...FADE_UP}
          transition={{ ...FADE_UP.transition, delay: 0.2 }}
          className="text-zinc-400 mt-6 max-w-xl mx-auto leading-relaxed"
        >
          {t('Sezonowe smaki gorącej czekolady, limitowane praliny i nowości — bez spamu, raz na jakiś czas.')}
        </motion.p>

        <motion.form
          {...FADE_UP}
          transition={{ ...FADE_UP.transition, delay: 0.3 }}
          onSubmit={submit}
          noValidate
          className="mt-12 max-w-lg mx-auto flex flex-col sm:flex-row gap-3 items-stretch"
        >
          <Input
            type="email"
            data-testid={SHOP.newsletterInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('twoj@email.pl')}
            className="flex-1 bg-transparent border border-white/15 rounded-none h-12 px-4 text-white text-base placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[#c07e5b]"
          />
          <button
            type="submit"
            data-testid={SHOP.newsletterSubmit}
            disabled={submitting}
            className="btn-copper px-8 h-12 text-xs tracking-[0.18em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? t('Zapisuję…') : t('Zapisz mnie')}
          </button>
        </motion.form>
        {done && (
          <p data-testid={SHOP.newsletterSuccess} className="mt-6 text-[#c07e5b] text-sm tracking-wide">
            {t('✓ Dziękujemy. Do zobaczenia w skrzynce.')}
          </p>
        )}
      </div>
    </section>
  );
}

function PralinySection() {
  const { t } = useLang();
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.min(el.clientWidth * 0.8, 500);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section
      id="praliny"
      data-testid={SHOP.pralinySection}
      className="relative bg-[#050505] py-32 md:py-44 border-t border-white/5 overflow-hidden"
    >
      {/* subtle radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(circle at 20% 30%, rgba(192,126,91,0.12), transparent 45%), radial-gradient(circle at 80% 70%, rgba(192,126,91,0.08), transparent 40%)',
        }}
      />

      <div className="relative">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...FADE_UP} className="max-w-3xl">
            <p className="label-eyebrow">{t('Praliny · kolekcja')}</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-6 leading-[0.95] tracking-tight">
              {t('Ręcznie robione, ')}<em className="text-[#c07e5b] italic font-light">{t('w małych partiach.')}</em>
            </h2>
            <p className="mt-8 text-zinc-400 text-lg leading-relaxed font-light">
              {t('Powstają w naszej manufakturze z prawdziwej pasji do czekolady i najwyższej jakości składników. Każda z nich to harmonijne połączenie rzemieślniczego kunsztu, głębokiego smaku i zachwycającej estetyki. To nie tylko doskonała słodka przyjemność, ale małe, czekoladowe dzieło sztuki.')}
            </p>
          </motion.div>
        </div>

        {/* Horizontal-scroll gallery of pralines */}
        <div className="relative mt-14 md:mt-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 md:mb-10">
            <p
              data-testid="praliny-gallery-note"
              className="text-white font-semibold text-base md:text-lg tracking-tight"
            >
              {t('Galeria przykładowych smaków pralin – pełna oferta dostępna na miejscu w lokalu.')}
            </p>
          </div>
          <div
            ref={scrollerRef}
            className="flex gap-4 md:gap-6 overflow-x-auto pb-6 px-6 md:px-12 snap-x snap-mandatory scrollbar-hide scroll-smooth"
          >
            {PRALINY_IMAGES.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="flex-shrink-0 w-72 md:w-96 aspect-square snap-center overflow-hidden"
              >
                <img
                  src={src}
                  alt={t('Praliny · kolekcja')}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1.6s] ease-out"
                />
              </motion.div>
            ))}
          </div>

          {/* Desktop scroll arrows */}
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label={t('Poprzednie')}
            data-testid="praliny-prev"
            className="hidden md:flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#0a0a0a]/85 backdrop-blur-md border border-white/15 text-white hover:bg-[#c07e5b] hover:text-[#0a0a0a] hover:border-transparent transition-all duration-500 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.7)]"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label={t('Następne')}
            data-testid="praliny-next"
            className="hidden md:flex items-center justify-center absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#0a0a0a]/85 backdrop-blur-md border border-white/15 text-white hover:bg-[#c07e5b] hover:text-[#0a0a0a] hover:border-transparent transition-all duration-500 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.7)]"
          >
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Footer note */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            {...FADE_UP}
            className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t border-white/5"
          >
            <div className="flex items-start gap-4 max-w-2xl">
              <div className="w-10 h-10 flex-shrink-0 rounded-full border border-[#c07e5b]/40 flex items-center justify-center text-[#c07e5b]">
                <Sparkles size={16} />
              </div>
              <p className="text-zinc-300 leading-relaxed">
                <span className="text-white font-medium">{t('Aktualne menu pralin dostępne na miejscu')}</span>{' '}
                <span className="text-zinc-500">{t('w Czekoladziarni — codziennie świeże, prosto z gabloty.')}</span>
              </p>
            </div>

            <a
              href={`tel:${SHOP_INFO.phoneRaw}`}
              className="btn-ghost-copper px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-3 whitespace-nowrap"
            >
              {t('Zapytaj o dostępność')} <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}



function Contact() {
  const { t } = useLang();
  return (
    <section id="kontakt" data-testid={SHOP.contactSection} className="relative bg-[#0a0a0a] pt-32 md:pt-48 pb-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.h2
          {...FADE_UP}
          className="font-display text-white leading-[0.85] tracking-tight"
          style={{ fontSize: 'clamp(3.5rem, 11vw, 11rem)' }}
        >
          {t('Wpadnij ')}<em className="italic font-light text-[#c07e5b]">{t('na chwilę')}</em>.
        </motion.h2>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div {...FADE_UP} className="lg:col-span-5 space-y-12">
            <div>
              <p className="label-eyebrow flex items-center gap-2"><MapPin size={12} /> {t('Adres')}</p>
              <p data-testid={SHOP.contactAddress} className="font-display text-3xl md:text-4xl text-white mt-3 leading-tight">
                {SHOP_INFO.address}
              </p>
              <p className="text-zinc-500 text-sm mt-2">{t('Stare Miasto · obok Rynku')}</p>
            </div>

            <div>
              <p className="label-eyebrow flex items-center gap-2"><Clock size={12} /> {t('Godziny otwarcia')}</p>
              <ul className="mt-4 space-y-2">
                {SHOP_INFO.hours.map((h) => (
                  <li key={h.day} className="flex items-baseline justify-between gap-6 text-zinc-300">
                    <span className="text-sm md:text-base font-light">{t(h.day)}</span>
                    <span className="flex-1 h-px bg-white/5 translate-y-[-4px]" />
                    <span className="text-[#c07e5b] tracking-wide text-sm md:text-base">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="label-eyebrow flex items-center gap-2"><Phone size={12} /> {t('Kontakt')}</p>
              <a
                href={`tel:${SHOP_INFO.phoneRaw}`}
                data-testid={SHOP.contactPhone}
                className="block font-display text-3xl md:text-4xl text-white hover:text-[#c07e5b] transition-colors mt-3"
              >
                {SHOP_INFO.phone}
              </a>
              <a
                href={`mailto:${SHOP_INFO.email}`}
                data-testid={SHOP.contactEmail}
                className="text-zinc-400 hover:text-[#c07e5b] transition-colors text-base mt-3 flex items-center gap-2"
              >
                <Mail size={14} /> {SHOP_INFO.email}
              </a>
            </div>

            <div className="flex gap-3 pt-4">
              <a
                href={SHOP_INFO.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid={SHOP.contactInstagram}
                className="w-12 h-12 border border-white/10 hover:border-[#c07e5b] hover:bg-[#c07e5b]/10 transition-all duration-500 flex items-center justify-center text-zinc-300 hover:text-[#c07e5b]"
                aria-label={t('Instagram')}
              >
                <Instagram size={18} />
              </a>
              <a
                href={SHOP_INFO.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid={SHOP.contactFacebook}
                className="w-12 h-12 border border-white/10 hover:border-[#c07e5b] hover:bg-[#c07e5b]/10 transition-all duration-500 flex items-center justify-center text-zinc-300 hover:text-[#c07e5b]"
                aria-label={t('Facebook')}
              >
                <Facebook size={18} />
              </a>
              <a
                href={SHOP_INFO.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid={SHOP.contactTiktok}
                className="w-12 h-12 border border-white/10 hover:border-[#c07e5b] hover:bg-[#c07e5b]/10 transition-all duration-500 flex items-center justify-center text-zinc-300 hover:text-[#c07e5b]"
                aria-label={t('TikTok')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.87a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.3Z"/>
                </svg>
              </a>
            </div>
          </motion.div>

          <motion.div {...FADE_UP} transition={{ ...FADE_UP.transition, delay: 0.15 }} className="lg:col-span-7">
            <div className="relative w-full aspect-[4/3] lg:aspect-square border border-white/10 overflow-hidden">
              <iframe
                title="Mapa Czekoladziarnia Wrocław"
                src={SHOP_INFO.mapsEmbed}
                className="absolute inset-0 w-full h-full grayscale contrast-125 brightness-75"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>

        <div className="mt-32 pt-10 border-t border-white/5 flex flex-wrap items-center justify-between gap-4 text-zinc-600 text-xs tracking-wide">
          <p>{t('© {year} Czekoladziarnia Wrocław · Ręcznie tworzona od 2004').replace('{year}', String(new Date().getFullYear()))}</p>
          <p>{t('ul. Więzienna 31 · Wrocław')}</p>
        </div>
      </div>
    </section>
  );
}

function useLenis() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

export default function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}

function AppInner() {
  useLenis();
  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen">
      <Toaster theme="dark" position="bottom-right" />
      <Nav />
      <main>
        <Hero />
        <MenuSection />
        <PralinySection />
        <Marquee />
        <Gallery />
        <Testimonials />
        <OrdersForm />
        <Newsletter />
        <Contact />
      </main>
    </div>
  );
}

